import {getSessionId} from './sessionManager'
import {PATH_TO_LABEL} from './pathToLabel'

const HEARTBEAT_INTERVAL_MS = 2 * 60 * 1000 // 2 minutes
const HEARTBEAT_URL = `${process.env.REACT_APP_API_ENDPOINT}external/ct/controltower/v1/wms/usage-analytics/heartbeat`
export const PRODUCT = 'proship'

let intervalId = null
let lastHeartbeatTime = Date.now()

// Circuit breaker: the analytics endpoint can be down (e.g. 503). fetch() does NOT reject on
// HTTP error statuses, so a bare try/catch never notices — we check response.ok explicitly and,
// after a few consecutive failures, stop calling it for the session so we don't fire a failing
// request on every page load / interval. Analytics is best-effort; degrading silently is fine.
let consecutiveFailures = 0
let heartbeatDisabled = false
const MAX_FAILURES = 3

// Reads accessToken from redux-persist localStorage entry without importing the store
// (avoids circular deps and keeps this module usable outside React)
function getToken() {
  try {
    const persisted = localStorage.getItem('persist:root')
    if (persisted) {
      const root = JSON.parse(persisted)
      const user = JSON.parse(root.user || '{}')
      return user.userInfo?.accessToken || null
    }
  } catch (e) {
    // silent
  }
  return null
}

// Replace dynamic path segments with canonical placeholders to prevent
// high-cardinality keys in analytics (e.g. one row per AWB number).
export function normaliseRoute(pathname) {
  if (!pathname) return pathname
  return pathname
    .replace(/\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, '/:id')
    .replace(/\/[A-Z]{2,}\d{6,}[A-Z0-9_-]*/g, '/:id') // AWB-style IDs e.g. PROZ1234567890
    .replace(/\/\d{4,}/g, '/:id') // pure numeric IDs 4+ digits
}

export function getPageMeta(pathname) {
  const label = PATH_TO_LABEL[pathname] || null
  if (!label) return {moduleName: null, pageName: null}
  const parts = label.split(' -> ')
  return {
    moduleName: parts[0] || null,
    pageName: parts[parts.length - 1] || null,
  }
}

async function sendHeartbeat(isSessionEnd = false, overrides = {}) {
  if (heartbeatDisabled) return
  const token = getToken()
  if (!token) return

  const now = Date.now()
  const activeSeconds = Math.round((now - lastHeartbeatTime) / 1000)
  lastHeartbeatTime = now

  const normalisedRoute = normaliseRoute(window.location.pathname)
  const {moduleName, pageName} = getPageMeta(normalisedRoute)

  const payload = {
    sessionId: getSessionId(),
    activeSeconds,
    currentRoute: normalisedRoute,
    sessionEnd: isSessionEnd,
    type: 'HEARTBEAT',
    event: 'PAGE_VIEW',
    product: PRODUCT,
    moduleName,
    pageName,
    ...overrides,
  }

  // sendBeacon is best-effort for tab-close events; keepalive fetch otherwise
  if (isSessionEnd && navigator.sendBeacon) {
    const blob = new Blob([JSON.stringify(payload)], {type: 'application/json'})
    navigator.sendBeacon(HEARTBEAT_URL, blob)
  } else {
    try {
      const res = await fetch(HEARTBEAT_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'X-Session-Id': getSessionId(),
        },
        body: JSON.stringify(payload),
        ...(isSessionEnd ? {keepalive: true} : {}),
      })
      // fetch resolves even on 5xx — treat a non-OK status as a failure too.
      if (!res || !res.ok) throw new Error(`heartbeat ${res && res.status}`)
      consecutiveFailures = 0
    } catch (e) {
      // never let heartbeat failures surface to the user; after a few in a row, give up
      // for the session so we stop firing a failing request on every load/interval.
      consecutiveFailures += 1
      if (consecutiveFailures >= MAX_FAILURES) {
        heartbeatDisabled = true
        if (intervalId) {
          clearInterval(intervalId)
          intervalId = null
        }
      }
    }
  }
}

function handleVisibilityChange() {
  if (document.hidden) {
    clearInterval(intervalId)
    intervalId = null
  } else if (!intervalId) {
    lastHeartbeatTime = Date.now()
    sendHeartbeat()
    intervalId = setInterval(() => sendHeartbeat(), HEARTBEAT_INTERVAL_MS)
  }
}

function handleBeforeUnload() {
  sendHeartbeat(true)
}

export function startHeartbeat() {
  if (intervalId) return // guard against double-init
  lastHeartbeatTime = Date.now()
  sendHeartbeat()
  intervalId = setInterval(() => sendHeartbeat(), HEARTBEAT_INTERVAL_MS)
  document.addEventListener('visibilitychange', handleVisibilityChange)
  window.addEventListener('beforeunload', handleBeforeUnload)
}

export function stopHeartbeat() {
  clearInterval(intervalId)
  intervalId = null
  document.removeEventListener('visibilitychange', handleVisibilityChange)
  window.removeEventListener('beforeunload', handleBeforeUnload)
}

export {sendHeartbeat}
